import axios from "axios";
import {
  ensureValidAccessToken,
  getSessionAccessToken,
  getStoredSession,
} from "utils/userIdentity";

const QUEUE_STORAGE_KEY = "OmnioOfflineQueue";
const CACHE_PREFIX = "OmnioCache:";
const CACHE_TTL_MS = 1000 * 60 * 60 * 24; // 24 hours
const FLUSH_INTERVAL_MS = 1000 * 60 * 10; // 10 minutes
const OFFLINE_NOTIFICATION_INTERVAL_MS = FLUSH_INTERVAL_MS;
const BG_SYNC_TAG = "omnio-offline-sync";
const QUEUE_CACHE_NAME = "OmnioOfflineQueueCache";
const QUEUE_CACHE_URL = "/__omnio_offline_queue__";
const RECORD_ID_KEYS = [
  "id",
  "Id",
  "ID",
  "recordId",
  "RecordId",
  "recordID",
  "rowId",
  "RowId",
  "RowID",
];
let lastOfflineQueueNotification = 0;

const shouldSurfaceOfflineNotice = () =>
  typeof navigator !== "undefined" && navigator.onLine === false;

const isBrowser = () => typeof window !== "undefined" && typeof window.localStorage !== "undefined";

const hasAuthorizationHeader = (headers) => {
  if (!headers) return false;
  if (headers.Authorization || headers.authorization) return true;
  if (typeof headers.has === "function" && headers.has("Authorization")) return true;
  return Object.keys(headers).some((key) => key?.toLowerCase() === "authorization");
};

const isCoreApiRequest = (config) => {
  const url = config?.url || "";
  if (!url || config?.__omnioTokenRefresh) return false;

  try {
    const resolved = new URL(url, window.location.origin);
    if (resolved.pathname.toLowerCase() === "/token") return false;
    const configuredCoreApiBaseUrl = process.env.REACT_APP_CORE_API_BASE_URL;
    if (configuredCoreApiBaseUrl) {
      const coreApiOrigin = new URL(configuredCoreApiBaseUrl, window.location.origin).origin;
      if (resolved.origin === coreApiOrigin) return true;
    }
    if (resolved.origin === "https://dvapi.omniofarm.com") return true;
    return resolved.origin === window.location.origin && url.startsWith("/");
  } catch (error) {
    return typeof url === "string" && url.startsWith("/") && url.toLowerCase() !== "/token";
  }
};

const shouldAttachAuthorization = (config) => {
  if (!config) return false;
  if (config.__omnioSkipAuthHeader) return false;
  if (config.omnioOffline?.skipAuthHeader && !isCoreApiRequest(config)) return false;
  const headers = config.headers || {};
  return !hasAuthorizationHeader(headers);
};

const safeJsonParse = (value) => {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch (error) {
    console.warn("Failed to parse JSON", error);
    return null;
  }
};

const safeJsonStringify = (value) => {
  try {
    return JSON.stringify(value);
  } catch (error) {
    console.warn("Failed to stringify JSON", error);
    return null;
  }
};

const parseQueuedPayload = (entry) => {
  if (!entry?.data) return null;
  const descriptor = entry.data;
  if (descriptor.type === "json") {
    if (!descriptor.body) return null;
    try {
      return JSON.parse(descriptor.body);
    } catch (error) {
      console.warn("Unable to parse queued JSON body", error);
      return null;
    }
  }
  if (descriptor.type === "string") {
    if (descriptor.body == null) return null;
    try {
      return JSON.parse(descriptor.body);
    } catch (error) {
      return null;
    }
  }
  return null;
};

const resolveRecordIdFromPayload = (payload) => {
  if (!payload || typeof payload !== "object") return null;
  for (const key of RECORD_ID_KEYS) {
    if (payload[key] !== undefined && payload[key] !== null) {
      return payload[key];
    }
  }
  if (payload.payload && typeof payload.payload === "object") {
    return resolveRecordIdFromPayload(payload.payload);
  }
  if (payload.data && typeof payload.data === "object") {
    return resolveRecordIdFromPayload(payload.data);
  }
  return null;
};

const getQueueEntryRecordKey = (entry) => {
  const payload = parseQueuedPayload(entry);
  const recordId = resolveRecordIdFromPayload(payload);
  if (recordId === undefined || recordId === null) return null;
  const method = (entry?.method || "post").toLowerCase();
  const base = entry?.baseURL || "";
  const url = entry?.url || "";
  return `${base}|${url}|${method}|${recordId}`;
};

const dedupeQueueEntries = (queue) => {
  if (!Array.isArray(queue)) return [];
  const keyed = new Map();
  const unkeyed = [];
  queue.forEach((entry) => {
    if (!entry) return;
    const key = getQueueEntryRecordKey(entry);
    if (!key) {
      unkeyed.push(entry);
      return;
    }
    const existing = keyed.get(key);
    if (!existing || (existing.timestamp || 0) <= (entry.timestamp || 0)) {
      keyed.set(key, entry);
    }
  });
  const deduped = [...unkeyed, ...keyed.values()];
  deduped.sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
  return deduped;
};

const readQueue = () => {
  if (!isBrowser()) return [];
  try {
    const raw = window.localStorage.getItem(QUEUE_STORAGE_KEY);
    const parsed = safeJsonParse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.warn("Unable to read offline queue", error);
    return [];
  }
};

const writeQueue = (queue) => {
  if (!isBrowser()) return;
  try {
    const normalizedQueue = dedupeQueueEntries(Array.isArray(queue) ? queue.filter(Boolean) : []);
    const serialized = safeJsonStringify(normalizedQueue);
    if (serialized) {
      window.localStorage.setItem(QUEUE_STORAGE_KEY, serialized);
      if (normalizedQueue.length > 0) {
        scheduleBackgroundSync();
      }
      persistQueueToCache(normalizedQueue);
    }
  } catch (error) {
    console.warn("Unable to persist offline queue", error);
  }
};

const scheduleBackgroundSync = () => {
  if (!isBrowser()) return;
  if (!("serviceWorker" in navigator)) return;
  navigator.serviceWorker.ready
    .then((registration) => {
      if (!registration?.sync || typeof registration.sync.register !== "function") {
        return flushOfflineQueue();
      }
      return registration.sync.register(BG_SYNC_TAG);
    })
    .catch((error) => {
      console.warn("Unable to register background sync", error);
      flushOfflineQueue();
    });
};

const persistQueueToCache = (queue) => {
  if (!isBrowser() || typeof caches === "undefined") return;
  const normalizedQueue = dedupeQueueEntries(Array.isArray(queue) ? queue.filter(Boolean) : []);
  caches
    .open(QUEUE_CACHE_NAME)
    .then((cache) => {
      if (!normalizedQueue.length) {
        return cache.delete(QUEUE_CACHE_URL);
      }
      const response = new Response(JSON.stringify(normalizedQueue), {
        headers: { "Content-Type": "application/json" },
      });
      return cache.put(QUEUE_CACHE_URL, response);
    })
    .catch((error) => {
      console.warn("Unable to persist offline queue cache", error);
    });
};

const hydrateQueueFromCache = async () => {
  if (!isBrowser() || typeof caches === "undefined") return;
  try {
    const cache = await caches.open(QUEUE_CACHE_NAME);
    const match = await cache.match(QUEUE_CACHE_URL);
    if (!match) {
      window.localStorage.removeItem(QUEUE_STORAGE_KEY);
      return;
    }
    const queue = await match.json();
    if (Array.isArray(queue)) {
      const normalizedQueue = dedupeQueueEntries(queue);
      if (normalizedQueue.length === 0) {
        window.localStorage.removeItem(QUEUE_STORAGE_KEY);
      } else {
        window.localStorage.setItem(QUEUE_STORAGE_KEY, JSON.stringify(normalizedQueue));
      }
    }
  } catch (error) {
    console.warn("Unable to hydrate offline queue from cache", error);
  }
};

const dispatchOfflineEvent = (name, detail) => {
  if (!isBrowser() || typeof window.dispatchEvent !== "function") return;
  window.dispatchEvent(new CustomEvent(name, { detail }));
};

const dispatchThrottledOfflineQueued = (detail) => {
  if (!isBrowser()) return;

  if (!shouldSurfaceOfflineNotice()) {
    lastOfflineQueueNotification = 0;
    return;
  }

  const now = Date.now();
  if (now - lastOfflineQueueNotification < OFFLINE_NOTIFICATION_INTERVAL_MS) {
    return;
  }
  lastOfflineQueueNotification = now;
  dispatchOfflineEvent("omnio:offline-queued", detail);
};

const serializeRequestConfig = (config) => {
  const method = (config.method || "get").toLowerCase();
  const headers = { ...(config.headers || {}) };
  const dataDescriptor = (() => {
    if (config.data == null) {
      return { type: "void", body: null };
    }
    if (typeof config.data === "string") {
      return { type: "string", body: config.data };
    }
    return { type: "json", body: safeJsonStringify(config.data) };
  })();

  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
    timestamp: Date.now(),
    url: config.url,
    baseURL: config.baseURL || null,
    method,
    headers,
    params: config.params || null,
    data: dataDescriptor,
  };
};

const reviveRequestConfig = (entry) => {
  const resolveUrl = () => {
    if (!entry.baseURL) return entry.url;
    try {
      return new URL(entry.url, entry.baseURL).toString();
    } catch (error) {
      console.warn("Failed to resolve queued request URL", error);
      return `${entry.baseURL.replace(/\/$/, "")}/${(entry.url || "").replace(/^\//, "")}`;
    }
  };

  let data;
  if (entry.data?.type === "json") {
    data = safeJsonParse(entry.data.body);
  } else if (entry.data?.type === "string") {
    data = entry.data.body;
  } else {
    data = null;
  }

  return {
    url: resolveUrl(),
    method: entry.method || "post",
    data,
    headers: entry.headers || {},
    params: entry.params || undefined,
    __skipOfflineQueue: true,
  };
};

const buildCacheKey = (config) => {
  const method = (config.method || "get").toLowerCase();
  const url = (() => {
    if (config.baseURL) {
      try {
        return new URL(config.url, config.baseURL).toString();
      } catch (error) {
        return `${config.baseURL.replace(/\/$/, "")}/${(config.url || "").replace(/^\//, "")}`;
      }
    }
    return config.url;
  })();

  const sortedParams = (() => {
    if (!config.params) return null;
    const sortObject = (obj) => {
      if (obj == null || typeof obj !== "object") return obj;
      if (Array.isArray(obj)) {
        return obj.map((item) => sortObject(item));
      }
      const sorted = {};
      Object.keys(obj)
        .sort()
        .forEach((key) => {
          sorted[key] = sortObject(obj[key]);
        });
      return sorted;
    };
    return sortObject(config.params);
  })();

  const paramsSegment = sortedParams ? safeJsonStringify(sortedParams) : "";
  return `${CACHE_PREFIX}${method}:${url}?${paramsSegment}`;
};

const cacheResponse = (config, response) => {
  if (!isBrowser()) return;
  if ((config.method || "get").toLowerCase() !== "get") return;
  if (response?.status && response.status >= 500) return;

  const key = buildCacheKey(config);
  const record = {
    timestamp: Date.now(),
    status: response?.status || 200,
    data: response?.data,
  };
  const serialized = safeJsonStringify(record);
  if (!serialized) return;
  try {
    window.localStorage.setItem(key, serialized);
  } catch (error) {
    console.warn("Unable to cache response", error);
  }
};

const getCachedResponse = (config) => {
  if (!isBrowser()) return null;
  const key = buildCacheKey(config);
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const record = safeJsonParse(raw);
    if (!record) return null;
    if (Date.now() - (record.timestamp || 0) > CACHE_TTL_MS) {
      window.localStorage.removeItem(key);
      return null;
    }
    return record;
  } catch (error) {
    console.warn("Unable to read cached response", error);
    return null;
  }
};

const enqueueOfflineRequest = async (config) => {
  const entry = serializeRequestConfig(config);
  const queue = dedupeQueueEntries(readQueue());
  queue.push(entry);
  writeQueue(queue);
  dispatchThrottledOfflineQueued({
    url: config.url,
    method: config.method,
    queuedAt: entry.timestamp,
  });
};

const shouldRetry = (error) => {
  if (!error) return false;
  if (error.response) {
    const status = error.response.status;
    return status >= 500 || status === 408 || status === 429;
  }
  return true;
};

export const flushOfflineQueue = async () => {
  if (!isBrowser() || !navigator.onLine) return;
  const queue = dedupeQueueEntries(readQueue());
  if (!queue.length) return;

  const remaining = [];
  for (const entry of queue) {
    try {
      const requestConfig = reviveRequestConfig(entry);
      await axios(requestConfig);
    } catch (error) {
      if (shouldRetry(error)) {
        remaining.push(entry);
      } else {
        console.error("Dropping offline entry", entry, error);
      }
    }
  }

  writeQueue(remaining);
  if (remaining.length === 0) {
    lastOfflineQueueNotification = 0;
  }
  dispatchOfflineEvent("omnio:offline-sync-complete", { remaining: remaining.length });
};

if (isBrowser() && "serviceWorker" in navigator) {
  navigator.serviceWorker.addEventListener("message", (event) => {
    const { data } = event || {};
    if (!data || typeof data !== "object") return;
    if (data.type === "OMNIO_BG_SYNC") {
      flushOfflineQueue();
      return;
    }
    if (data.type === "OMNIO_QUEUE_UPDATE") {
      const queue = Array.isArray(data.queue) ? data.queue : [];
      try {
        const normalizedQueue = dedupeQueueEntries(queue);
        if (normalizedQueue.length === 0) {
          window.localStorage.removeItem(QUEUE_STORAGE_KEY);
        } else {
          window.localStorage.setItem(QUEUE_STORAGE_KEY, JSON.stringify(normalizedQueue));
        }
        persistQueueToCache(normalizedQueue);
        if (normalizedQueue.length > 0) {
          scheduleBackgroundSync();
        }
      } catch (error) {
        console.warn("Unable to update offline queue from service worker", error);
      }
      return;
    }
  });
}

const shouldQueueRequest = (config, error) => {
  const method = (config.method || "get").toLowerCase();
  if (!["post", "put", "patch", "delete"].includes(method)) return false;
  if (config.__skipOfflineQueue || config.omnioOffline?.skipQueue) return false;

  const offline = typeof navigator !== "undefined" && navigator && navigator.onLine === false;
  const networkError =
    !error?.response && (error?.code === "ERR_NETWORK" || error?.message?.includes("Network"));
  return offline || networkError;
};

const shouldServeCachedResponse = (config, error) => {
  if ((config.method || "get").toLowerCase() !== "get") return false;
  if (config.omnioOffline?.skipCache) return false;
  const offline = typeof navigator !== "undefined" && navigator && navigator.onLine === false;
  const networkError =
    !error?.response && (error?.code === "ERR_NETWORK" || error?.message?.includes("Network"));
  return offline || networkError;
};

export const setupOfflineSupport = () => {
  if (!isBrowser()) return;

  hydrateQueueFromCache();
  persistQueueToCache(readQueue());

  axios.interceptors.request.use(async (config) => {
    const session = getStoredSession();
    if (getSessionAccessToken(session) && shouldAttachAuthorization(config)) {
      const headers = config.headers || {};
      const token = await ensureValidAccessToken();
      if (token) {
        headers.Authorization = `Bearer ${token}`;
        headers["X-Omnio-Authorization"] = `Bearer ${token}`;
      }
      config.headers = headers;
    }
    return config;
  });

  axios.interceptors.response.use(
    (response) => {
      if (response?.config) {
        cacheResponse(response.config, response);
      }
      return response;
    },
    async (error) => {
      const config = error?.config;
      if (!config) {
        return Promise.reject(error);
      }

      if (shouldQueueRequest(config, error)) {
        await enqueueOfflineRequest(config);
        return Promise.resolve({
          data: { offline: true, queued: true },
          status: 202,
          statusText: "Accepted (offline)",
          headers: {},
          config,
          request: null,
        });
      }

      if (shouldServeCachedResponse(config, error)) {
        const cached = getCachedResponse(config);
        if (cached) {
          return Promise.resolve({
            data: cached.data,
            status: cached.status || 200,
            statusText: "OK (cached)",
            headers: {},
            config,
            request: null,
          });
        }
      }

      return Promise.reject(error);
    }
  );

  flushOfflineQueue();
  if (readQueue().length > 0) {
    scheduleBackgroundSync();
  }

  if (!window.__omnioOfflineFlushInterval) {
    window.__omnioOfflineFlushInterval = window.setInterval(() => {
      flushOfflineQueue();
    }, FLUSH_INTERVAL_MS);
  }

  window.addEventListener("online", () => {
    flushOfflineQueue();
  });

  window.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      flushOfflineQueue();
    }
  });
};
